import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import apiClient from '@/api/client';
import { User } from '@/types';
import toast from 'react-hot-toast';

interface AuthStore {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  magicLinkSent: boolean;
  authMethods: any | null;
  
  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, fullName: string) => Promise<void>;
  sendMagicLink: (email: string, name?: string) => Promise<void>;
  verifyMagicLink: (email: string, code: string) => Promise<void>;
  googleAuth: (accessToken: string) => Promise<void>;
  logout: () => void;
  fetchUser: () => Promise<void>;
  setUser: (user: User) => void;
  getAuthMethods: () => Promise<void>;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      magicLinkSent: false,
      authMethods: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const formData = new FormData();
          formData.append('username', email);
          formData.append('password', password);
          
          const response = await apiClient.post('/api/auth/token', formData, {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
          });
          
          const { access_token, user } = response.data;
          
          set({
            token: access_token,
            user,
            isAuthenticated: true,
          });
          
          toast.success('Login successful!');
        } catch (error: any) {
          const message = error.response?.data?.error || 'Login failed';
          toast.error(message);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      register: async (email: string, password: string, fullName: string) => {
        set({ isLoading: true });
        try {
          const response = await apiClient.post('/api/auth/register', {
            email,
            password,
            full_name: fullName,
          });
          
          toast.success('Registration successful! Please login.');
        } catch (error: any) {
          const message = error.response?.data?.error || 'Registration failed';
          toast.error(message);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      sendMagicLink: async (email: string, name?: string) => {
        set({ isLoading: true });
        try {
          await apiClient.post('/api/auth/magic-link', { email, name });
          set({ magicLinkSent: true });
          toast.success('Magic link sent! Check your email.');
        } catch (error: any) {
          toast.error(error.response?.data?.message || 'Failed to send magic link');
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      verifyMagicLink: async (email: string, code: string) => {
        set({ isLoading: true });
        try {
          const response = await apiClient.post('/api/auth/verify-magic-link', {
            email,
            code,
          });
          
          const { access_token, user } = response.data;
          
          set({
            token: access_token,
            user,
            isAuthenticated: true,
            magicLinkSent: false,
          });
          
          toast.success('Welcome to Joblyn!');
        } catch (error: any) {
          toast.error(error.response?.data?.message || 'Invalid or expired link');
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          magicLinkSent: false,
        });
        toast.success('Logged out successfully');
      },

      fetchUser: async () => {
        if (!get().token) return;
        
        try {
          console.log('🔄 Fetching user data from /api/auth/me');
          const response = await apiClient.get('/api/auth/me');
          console.log('📥 Received user data:', JSON.stringify(response.data, null, 2));
          set({ user: response.data });
          console.log('✅ Auth store updated with user data');
        } catch (error) {
          console.error('Failed to fetch user:', error);
          get().logout();
        }
      },

      googleAuth: async (accessToken: string) => {
        set({ isLoading: true });
        try {
          const response = await apiClient.post('/api/auth/google', {
            access_token: accessToken,
          });
          
          const { access_token, user } = response.data;
          
          set({
            token: access_token,
            user,
            isAuthenticated: true,
          });
          
          toast.success(response.data.message || 'Google login successful!');
        } catch (error: any) {
          const message = error.response?.data?.detail || 'Google authentication failed';
          toast.error(message);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      setUser: (user: User) => {
        set({ user });
      },

      getAuthMethods: async () => {
        try {
          const response = await apiClient.get('/api/auth/auth-methods');
          set({ authMethods: response.data });
        } catch (error) {
          console.error('Failed to fetch auth methods:', error);
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        authMethods: state.authMethods,
      }),
    }
  )
);
